import Form from "@/components/ui/forms/Form.tsx";
import routes from "@/config/routes";
import Link from "@/components/ui/links/Link.tsx";
import { PasswordResetUserInput } from "@/types";
import BackIcon from "@/components/icons/BackIcon.tsx";
import { usePasswordResetBetaMutation } from "@/data/user.ts";
import OutlinedInput from "@edusogno/inputs/outlined-input";
import AuthBar from "@edusogno/layouts/auth-bar";
import AuthPageFooter from "@edusogno/layouts/auth-page-footer";
import AuthPageHeading from "@edusogno/layouts/auth-page-heading";
import { cn } from "@edusogno/lib/utils";
import LoadingButton from "@edusogno/primitives/loading-button";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import Warning from "../ui/Warning";
import { useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import { yupResolver } from "@hookform/resolvers/yup";
// import { useNavigate, useParams } from "react-router-dom";
// import RequestClient from "@/data/client";

export default function ResetPasswordForm() {
  const { mutate: reset, isLoading } = usePasswordResetBetaMutation();
  // const { token } = useParams<{ token: string }>();
  // const navigate = useNavigate();

  const { t } = useTranslation();

  const passwordResetValidationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t("auth.errors.valid_email"))
      .required(t("auth.errors.email_required")),
  });

  const {
    getFieldState,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordResetUserInput>({
    mode: "onChange",
    defaultValues: {
      email: "",
    },
    resolver: yupResolver(passwordResetValidationSchema),
  });

  const onSubmit: SubmitHandler<PasswordResetUserInput> = (data) => {
    reset(data);
  };

  const emailFieldState = getFieldState("email");
  const hasErrors = emailFieldState.isTouched && emailFieldState?.invalid;

  useEffect(() => {
    if (errors.email && hasErrors) {
      enqueueSnackbar(errors.email.message, {
        variant: "error",
      });
    }
  }, [emailFieldState, errors, getFieldState, hasErrors]);

 
  return (
    <Form<PasswordResetUserInput>
      onSubmit={
        handleSubmit(
          onSubmit,
        ) as unknown as SubmitHandler<PasswordResetUserInput>
      }
    >
      {() => (
        <>
          <AuthBar progress={0} onClick={() => {}} />
          <div
            className={
              "flex flex-col flex-[4_0_40%]  lg:max-w-[55%] lg:min-w-[500px]  pt-6 2xl:pt-8  mx-auto gap-3  px-5 md:gap-4  w-full items-center "
            }
          >
            <AuthPageHeading
              title={t("auth.reset.text")}
              subtitle={t("auth.reset.subtext")}
            />
            <div
              className={cn(
                "flex flex-col flex-[1_1_100%]  3xl:pt-12 w-full gap-4 h-full",
              )}
            >
              <OutlinedInput
                label={t("auth.labels.email")}
                placeholder={"hello@you.com"}
                autoComplete={"off"}
                type="email"
                {...register("email")}
                // className={"my-auto"}
                // size={"lg"}
                color={"grey"}
                error={Boolean(errors.email?.message) as boolean}
              />
              <Warning
                text="If the email provided is registered, a reset link will be sent
                to your email. It can take a few minutes"
              />
            </div>
          </div>
          <AuthPageFooter>
            <div
              className={
                "font-normal text-lg flex flex-row items-center justify-center md:text-lg  pt-4 mb-3 text-center text-brand w-full px-1"
              }
            >
              <Link to={routes.login}>
                <div
                  className={
                    "flex flex-row  lg:mb-0 items-center justify-end gap-2"
                  }
                >
                  <BackIcon className={"w-5 h-5"} />
                  {t("auth.login.back")}
                </div>
              </Link>
            </div>
            <div
              className={
                "w-full max-w-md mx-auto flex flex-col items-center px-6  mb-4"
              }
            >
              <LoadingButton
                size={"lg"}
                type="submit"
                className="w-full text-lg tracking-wide hidden md:block"
                loading={isLoading}
                disabled={isLoading || hasErrors}
              >
                {t("auth.login.send")}
              </LoadingButton>
              <LoadingButton
                size={"md"}
                type="submit"
                className="w-full text-lg tracking-wide block md:hidden"
                loading={isLoading}
                disabled={isLoading || hasErrors}
              >
                {t("auth.login.send")}
              </LoadingButton>
            </div>
          </AuthPageFooter>
        </>
      )}
    </Form>
  );
}
